import { computed, defineComponent, ref, watch, type SetupContext } from "vue";
import { useI18n } from "vue-i18n";
import html_iway from "./template.iway.html?raw";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}

interface Country {
  code: string;
  dialCode: string;
  flag: string;
  nameKey: string;
}

const DEFAULT_COUNTRY_CODE = "CH";

// "0" + längste unterstützte nationale Nummer (DE/AT bis zu 11 signifikante Ziffern)
const MAX_NATIONAL_DIGITS = 13;

// Nach Länge des Wahlcodes absteigend, damit z.B. +423 (Liechtenstein) nicht
// faelschlicherweise als CH/AT/DE (2-stellige Codes) erkannt wird
const COUNTRIES: Country[] = [
  { code: "CH", dialCode: "41", flag: "🇨🇭", nameKey: "InternationalNumberEntry_Country_CH" },
  { code: "LI", dialCode: "423", flag: "🇱🇮", nameKey: "InternationalNumberEntry_Country_LI" },
  { code: "AT", dialCode: "43", flag: "🇦🇹", nameKey: "InternationalNumberEntry_Country_AT" },
  { code: "DE", dialCode: "49", flag: "🇩🇪", nameKey: "InternationalNumberEntry_Country_DE" },
  { code: "FR", dialCode: "33", flag: "🇫🇷", nameKey: "InternationalNumberEntry_Country_FR" },
  { code: "IT", dialCode: "39", flag: "🇮🇹", nameKey: "InternationalNumberEntry_Country_IT" },
];

const countryByCode = (code: string): Country =>
  COUNTRIES.find((c) => c.code == code) ?? COUNTRIES.find((c) => c.code == DEFAULT_COUNTRY_CODE)!;

const formatNational = (digits: string): string => {
  const parts = [
    digits.slice(0, 3),
    digits.slice(3, 6),
    digits.slice(6, 8),
    digits.slice(8, 10),
  ].filter((part) => part.length > 0);
  let rest = digits.slice(10);
  while (rest.length > 0) {
    parts.push(rest.slice(0, 2));
    rest = rest.slice(2);
  }
  return parts.join(" ");
};

// Erkennt Land + nationale Nummer (mit führender 0) aus den Ziffern nach einem "+".
// candidates schränkt die erkennbaren Länder ein (z.B. nur CH bei swissOnly)
const matchInternational = (digitsAfterPlus: string, candidates: Country[]): { country: Country; nationalDigits: string } | null => {
  for (const country of candidates) {
    if (digitsAfterPlus.startsWith(country.dialCode) && digitsAfterPlus.length > country.dialCode.length) {
      const significant = digitsAfterPlus.slice(country.dialCode.length);
      return { country, nationalDigits: ("0" + significant).slice(0, MAX_NATIONAL_DIGITS) };
    }
  }
  return null;
};

// Baut aus Land + national eingegebener Nummer (inkl. führender 0) den zu speichernden internationalen Wert
const toE164 = (country: Country, nationalDigits: string): string | null => {
  const significant = nationalDigits.replace(/^0+/, "");
  if (0 == significant.length) {
    return null;
  }
  return `+${country.dialCode}${significant}`;
};

// Zerlegt einen gespeicherten internationalen Wert (z.B. +41987654321) in Land + nationale Anzeige
const fromE164 = (value: string | null, candidates: Country[]): { country: Country; nationalDigits: string } => {
  const raw = value ?? "";
  if (raw.startsWith("+")) {
    const matched = matchInternational(raw.slice(1).replace(/\D/g, ""), candidates);
    if (matched) {
      return matched;
    }
  }
  return { country: candidates[0] ?? countryByCode(DEFAULT_COUNTRY_CODE), nationalDigits: raw.replace(/\D/g, "") };
};

let instanceCounter = 0;

interface Props {
  modelValue: string | null;
  label: string;
  error: string;
  className: string;
  swissOnly: boolean;
}

export default defineComponent({
  template: html,
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      required: true,
    },
    error: {
      type: String,
      default: "",
    },
    className: {
      type: String,
      default: "",
    },
    // Wenn gesetzt, ist nur die Schweizer Vorwahl wählbar/erkennbar
    swissOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    "update:modelValue": (_value: string | null) => true,
    change: () => true,
  },
  setup(props: Props, { emit }: SetupContext) {
    const { t } = useI18n();

    // eindeutige ID, da diese Komponente mehrfach gleichzeitig gerendert werden kann
    const inputId = `international-number-entry-${++instanceCounter}`;

    const availableCountries = computed(() =>
      props.swissOnly ? COUNTRIES.filter((c) => "CH" == c.code) : COUNTRIES);

    const initial = fromE164(props.modelValue, availableCountries.value);
    const selectedCountryCode = ref<string>(initial.country.code);
    const displayValue = ref<string>(formatNational(initial.nationalDigits));

    const selectedCountry = computed(() => countryByCode(selectedCountryCode.value));

    watch(() => props.modelValue, (value) => {
      const currentE164 = toE164(selectedCountry.value, displayValue.value.replace(/\D/g, ""));
      if (value != currentE164) {
        const parsed = fromE164(value, availableCountries.value);
        selectedCountryCode.value = parsed.country.code;
        displayValue.value = formatNational(parsed.nationalDigits);
      }
    });

    const emitValue = () => {
      const digits = displayValue.value.replace(/\D/g, "");
      emit("update:modelValue", toE164(selectedCountry.value, digits));
    };

    // Wechselt swissOnly zur Laufzeit auf true, während ein anderes Land gewählt war -> auf CH zurücksetzen
    watch(availableCountries, (candidates) => {
      if (!candidates.some((c) => c.code == selectedCountryCode.value)) {
        selectedCountryCode.value = candidates[0]?.code ?? DEFAULT_COUNTRY_CODE;
        emitValue();
      }
    });

    const localError = computed(() => {
      const digits = displayValue.value.replace(/\D/g, "");
      if (0 == digits.length) {
        return "";
      }
      if (digits.length < 5 || digits.length > MAX_NATIONAL_DIGITS || "0" != digits[0]) {
        return t("InternationalNumberEntry_Invalid");
      }
      return "";
    });

    const displayedError = computed(() => localError.value || props.error);

    // Formatiert während der Eingabe; erkennt eine direkt eingegebene internationale Nummer
    // (z.B. +41... oder 0041...) und wechselt dann automatisch die Länderflagge
    const onInput = (evt: Event) => {
      const target = evt.target as HTMLInputElement;
      let raw = target.value;

      if (raw.startsWith("00")) {
        raw = "+" + raw.slice(2);
      }

      if (raw.startsWith("+")) {
        const digitsAfterPlus = raw.slice(1).replace(/\D/g, "");
        const matched = matchInternational(digitsAfterPlus, availableCountries.value);
        if (matched) {
          selectedCountryCode.value = matched.country.code;
          displayValue.value = formatNational(matched.nationalDigits);
          target.value = displayValue.value;
          emitValue();
          return;
        }
        // Wahlcode noch unvollständig - unformatiert stehen lassen, bis er erkennbar ist
        target.value = raw;
        return;
      }

      const digits = raw.replace(/\D/g, "").slice(0, MAX_NATIONAL_DIGITS);
      displayValue.value = formatNational(digits);
      target.value = displayValue.value;
      emitValue();
    };

    const onCountryChange = () => {
      emitValue();
      emit("change");
    };

    const onChange = () => {
      emit("change");
    };

    return {
      t,
      inputId,
      countries: availableCountries,
      selectedCountryCode,
      displayValue,
      displayedError,
      onInput,
      onChange,
      onCountryChange,
    };
  },
} as any);
